/**
 * The router's system info, turned into things an operator can read.
 *
 * Kept pure and separate from the card for the same reason deviceDiagText.ts is:
 * this arithmetic is the whole feature. A percentage computed the obvious way is
 * wrong by a factor of four on the actual hardware, and nobody looking at a
 * finished card can tell.
 *
 * 🔴 THE RULE THIS FILE EXISTS TO ENFORCE: a router that did not say must never
 * render as a router that said zero.
 *
 * Every input is `number | null | undefined`, and every function returns `null`
 * rather than a number when it was not told enough to answer. The card then says
 * so in words. This is not defensive habit — `diag.freeKb` defaults to 0, and on
 * 5D ALCARDE that made a router which had never reported its free space look
 * like a router with none left. Two days went into a disk that was never full.
 * 48 routers in the field report none of this today.
 */

export interface RawSys {
   /** 1-minute load average × 100. 47 means a load of 0.47. */
   loadCenti?: number | null;
   cores?: number | null;
   /** CPU busy % over the last beat, from /proc/stat. Newer agents only. */
   busyPct?: number | null;
   memTotalKb?: number | null;
   memAvailKb?: number | null;
   cfgFreeKb?: number | null;
   cfgTotalKb?: number | null;
   dlFreeKb?: number | null;
   dlTotalKb?: number | null;
   clients?: number | null;
   at?: string | null;
}

const has = (v: number | null | undefined): v is number =>
   typeof v === 'number' && Number.isFinite(v);

/**
 * How busy the processor is, as a percentage.
 *
 * 🔴 THE LOAD FIGURE ALONE IS NOT A PERCENTAGE, AND READING IT AS ONE IS THE
 * WHOLE REASON THIS FUNCTION EXISTS. A load average of 0.47 is not "47% busy".
 * It is 0.47 of ONE cpu's worth of work, and the Ruijie has four — so it is
 * about 12% busy. Printing 47 would paint a nearly idle router as half loaded,
 * and an operator would go looking for a problem that is not there.
 *
 * Returns null unless BOTH halves arrived. A load without its core count cannot
 * be turned into a percentage by guessing the divisor, and guessing 1 is exactly
 * the four-fold error above.
 */
export function cpuPercent(
   loadCenti: number | null | undefined,
   cores: number | null | undefined,
   busyPct?: number | null
): number | null {
   // A measured busy % beats the load-derived one whenever the agent sent it.
   // Load ÷ cores pins a 1-core board at 100% from load 1.0 upward while it
   // is actually half idle (Comfast bench 2026-09-11: load 1.4, 54% idle), and
   // load counts processes waiting on disk/network, not just the CPU.
   if (has(busyPct) && busyPct >= 0) return Math.min(100, Math.round(busyPct));
   if (!has(loadCenti) || !has(cores) || cores <= 0 || loadCenti < 0) return null;
   // Capped at 100 for the ring's sake. A load above the core count is real and
   // means the router is oversubscribed, but a ring cannot draw past full — the
   // caption beside it always carries the raw load, so nothing is hidden.
   return Math.min(100, Math.round(loadCenti / cores));
}

/**
 * How much memory is in use, as a percentage.
 *
 * 🔴 Derived from AVAILABLE, never from free. Measured on the Ruijie: 39MB free
 * against 29MB available, because the kernel counts its caches as free while a
 * program that needs memory cannot have them. The agent sends MemAvailable and
 * nothing else on purpose; this is the other half of that decision.
 */
export function memPercent(
   totalKb: number | null | undefined,
   availKb: number | null | undefined
): number | null {
   if (!has(totalKb) || !has(availKb) || totalKb <= 0) return null;
   const used = Math.max(0, totalKb - availKb);
   return Math.min(100, Math.round((used / totalKb) * 100));
}

export type Tone = 'ok' | 'warn' | 'bad';

/**
 * Green below 60, amber to 85, red above.
 *
 * Used for the PROCESSOR and for the two disk rows. A processor sitting at 60%
 * on a router that mostly forwards packets is worth a look, and a writable area
 * 60% full is worth knowing about before it stops an update.
 *
 * NOT used for memory — see memTone.
 */
export function tone(pct: number | null): Tone {
   if (pct === null) return 'ok';
   if (pct >= 85) return 'bad';
   if (pct >= 60) return 'warn';
   return 'ok';
}

/**
 * The same idea for MEMORY, with different numbers, because it is a different
 * question.
 *
 * 🔴 MEASURED, on RJE-59B421B6, with one phone connected: 35.6MB available of
 * 116MB — 69% used. Every megabyte of it is accounted for by hostapd (5.1MB),
 * dnsmasq (5.0MB), wpa_supplicant (4.8MB) and netifd/procd/logd (7.6MB). That
 * is the fixed cost of being a router; it reads the same at zero phones as at
 * thirty, because clients cost a DHCP lease and some conntrack entries, not
 * megabytes.
 *
 * Under the processor's bands, 69% is amber — so EVERY healthy router in the
 * fleet would fly an alarm colour, permanently. An alarm that is always on is
 * one nobody reads, and it would bury the single box that is genuinely in
 * trouble. Linux is supposed to use its memory; unused memory is wasted memory.
 *
 * So this stays quiet until a box is actually near the point of struggling.
 *
 * Ray spotted this the moment the first real card rendered: "is this normal?
 * RAM usage? in only 1 phone?" — which is exactly the question a wrongly-tuned
 * threshold provokes.
 */
export function memTone(pct: number | null): Tone {
   if (pct === null) return 'ok';
   if (pct >= 93) return 'bad';
   if (pct >= 85) return 'warn';
   return 'ok';
}

/**
 * "5.5 MB" · "116 MB" · "912 KB"
 *
 * Under a megabyte it stays in KB rather than rendering "0.9 MB": the settings
 * area is about 5.5MB in total, so the moment it matters — the moment it is
 * nearly full — the interesting numbers are small ones, and one decimal place of
 * a megabyte is too coarse to show the difference between "tight" and "stuck".
 */
export function humanKb(kb: number | null | undefined): string | null {
   if (!has(kb) || kb < 0) return null;
   if (kb < 1024) return `${Math.round(kb)} KB`;
   const mb = kb / 1024;
   return `${mb >= 100 ? Math.round(mb) : Math.round(mb * 10) / 10} MB`;
}

export interface SpaceRow {
   /** "5.1 of 5.5 MB free" — or "5.1 MB free" when the total was not reported. */
   text: string;
   /** How FULL it is, for the bar. A nearly-full bar reads as a warning at a
    *  glance, which is the only reason the bar is there. Null = no total, so
    *  there is no scale and the bar must not be drawn at all. */
   usedPct: number | null;
   tone: Tone;
}

/**
 * One storage row.
 *
 * Returns null when the router said nothing — NOT a "0 MB free" row, which is
 * the single most dangerous thing this screen could show. "No space left" is the
 * alarm that sends someone to a shop.
 *
 * A free figure with no total is still worth showing: 2.1.6-era routers report
 * their free space and not their partition size, which is most of the fleet, and
 * "5.1 MB free" is a true and useful sentence. It just gets no bar, because
 * without a total there is no scale to draw one against.
 */
export function spaceRow(
   freeKb: number | null | undefined,
   totalKb: number | null | undefined
): SpaceRow | null {
   if (!has(freeKb) || freeKb < 0) return null;
   const free = humanKb(freeKb);
   if (free === null) return null;

   if (!has(totalKb) || totalKb <= 0) {
      return { text: `${free} free`, usedPct: null, tone: 'ok' };
   }
   // A misread pair (more free than the partition holds) is dropped server-side,
   // but the client must not draw a bar past the end of its track if one ever
   // reaches here.
   if (freeKb > totalKb) return { text: `${free} free`, usedPct: null, tone: 'ok' };

   const usedPct = Math.min(100, Math.round(((totalKb - freeKb) / totalKb) * 100));
   const total = humanKb(totalKb) as string;

   // "5.1 of 5.5 MB free" when both sides share a unit, but "176 KB of 5.5 MB
   // free" when they do not. Dropping the unit unconditionally is what makes
   // the nearly-full case unreadable: "176 of 5.5 MB free" invites the eye to
   // compare 176 against 5.5 and conclude there is plenty of room, which is the
   // exact opposite of what that row is trying to say.
   const unit = (s: string) => s.slice(s.indexOf(' ') + 1);
   const text =
      unit(free) === unit(total)
         ? `${free.slice(0, free.indexOf(' '))} of ${total} free`
         : `${free} of ${total} free`;

   return { text, usedPct, tone: tone(usedPct) };
}

/**
 * What this card can actually show for this router.
 *
 *   'live'    — it reported its processor and memory. Rings and rows.
 *   'partial' — it reported some of the rest but not the vitals. This is most
 *               of the fleet: a 2.1.6-era router knows its writable space and
 *               its restarts, and nothing about CPU.
 *   'silent'  — it has told us nothing of this at all (2.0.x, ~48 routers).
 *
 * 🔴 KEYED ON WHAT ARRIVED, NEVER ON A VERSION STRING. A version comparison
 * would have to be kept in step with every board, every bundle and every
 * hand-flashed unit, and it would be wrong in exactly the interesting cases —
 * the router that is on a new version but whose reading failed. What we have is
 * what we can show; that question answers itself from the data.
 *
 * The middle state is the one worth having. Blanking the whole card unless a
 * router can do everything would hide the storage and restart figures we have
 * held for months on hundreds of routers.
 */
export function sysState(
   sys: RawSys | null | undefined,
   diagFreeKb?: number | null,
   /**
    * 🔴 WHICH HARDWARE THIS IS, because "cannot" and "not yet" are different
    * answers and only one of them asks the owner to do something.
    *
    * A MikroTik runs none of our agent software — these numbers reach us on the
    * OpenWRT agent's heartbeat, which a MikroTik does not have. So no firmware
    * update will ever make one report this, and telling its owner to update is
    * telling them to do something that cannot work. Measured on prod
    * 2026-08-30: 68 MikroTik main vendos would have been given that instruction.
    *
    * Same family of mistake as rendering silence as a zero: the screen inventing
    * a cause it does not have.
    */
   platform?: string | null
): 'live' | 'partial' | 'silent' | 'unsupported' {
   const vitals =
      cpuPercent(sys?.loadCenti, sys?.cores, sys?.busyPct) !== null ||
      memPercent(sys?.memTotalKb, sys?.memAvailKb) !== null;
   if (vitals) return 'live';

   const anythingElse =
      has(sys?.cfgFreeKb) ||
      has(sys?.dlFreeKb) ||
      has(sys?.clients) ||
      // The pre-existing channel: routers have reported their writable space on
      // `diag` since 2.1.6, long before this card existed. 0 there means "did
      // not say" (it defaults to 0), so only a positive number counts as spoken.
      (has(diagFreeKb) && diagFreeKb > 0);

   if (anythingElse) return 'partial';

   // Data always beats the label: the checks above run FIRST, so if a MikroTik
   // ever did report something we show what it said rather than a message
   // insisting it cannot.
   return platform === 'mikrotik' ? 'unsupported' : 'silent';
}

/**
 * "8 phones" · "1 phone" · "none right now" — and null when the router cannot
 * tell us, which is a different answer from an empty shop and must not print as
 * one.
 */
export function phonesLine(clients: number | null | undefined): string | null {
   if (!has(clients) || clients < 0) return null;
   if (clients === 0) return 'None right now';
   return `${clients} phone${clients === 1 ? '' : 's'}`;
}
